import { useEffect, useState } from "react";
import { Eye, EyeOff, Loader2, Plug, Plus, Save, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoNote, SectionCard } from "@/components/common/Cards";

const newRule = () => ({ key: Math.random().toString(36).slice(2), id: null, label: "", subject: "", sender: "", pdf_password: "", has_password: false, clear_password: false });

export function GmailSettingsTab({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [appPw, setAppPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/cc/config");
      setCfg({ ...data, rules: (data.rules || []).map((r) => ({ ...r, key: r.id, pdf_password: "", clear_password: false })) });
    } catch (e) {
      toast.error(errMsg(e));
      setCfg({ gmail_address: "", has_app_password: false, lookback_days: 60, rules: [] });
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (!cfg) return <Skeleton className="h-80 rounded-xl bg-[hsl(220_10%_11%)]" />;

  const setRule = (key, patch) => setCfg((c) => ({ ...c, rules: c.rules.map((r) => (r.key === key ? { ...r, ...patch } : r)) }));

  const save = async () => {
    const bad = cfg.rules.find((r) => r.subject.trim().length < 2);
    if (bad) return toast.error("Setiap aturan wajib punya kata kunci judul email (min. 2 huruf).");
    setSaving(true);
    try {
      const { data } = await api.put("/cc/config", {
        gmail_address: cfg.gmail_address,
        app_password: appPw,
        lookback_days: Number(cfg.lookback_days) || 60,
        rules: cfg.rules.map((r) => ({ id: r.id, label: r.label, subject: r.subject, sender: r.sender, pdf_password: r.pdf_password, clear_password: r.clear_password })),
      });
      setAppPw("");
      setCfg({ ...data, rules: data.rules.map((r) => ({ ...r, key: r.id, pdf_password: "", clear_password: false })) });
      toast.success("Pengaturan Gmail disimpan");
      onSaved && onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    } finally {
      setSaving(false);
    }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/cc/config/test");
      toast.success(data.message);
    } catch (e) {
      toast.error(errMsg(e, "Koneksi gagal"));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="grid xl:grid-cols-[1fr_380px] gap-5 items-start">
      <div className="space-y-5 min-w-0">
        <SectionCard title="Akun Gmail" description="Dipakai untuk mencari email tagihan dan mengunduh lampiran PDF (akses baca saja)." testid="cc-gmail-account">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Alamat Gmail</Label>
              <Input value={cfg.gmail_address} onChange={(e) => setCfg({ ...cfg, gmail_address: e.target.value })} placeholder="nama@gmail.com" className="mt-1 h-10 bg-[hsl(220_12%_8%)]" data-testid="cc-gmail-address" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-2">
                App Password {cfg.has_app_password && <Badge variant="outline" className="text-[9px] border-[hsl(160_40%_30%)] text-[hsl(160_55%_60%)]">tersimpan</Badge>}
              </Label>
              <div className="relative mt-1">
                <Input
                  type={showPw ? "text" : "password"}
                  value={appPw}
                  onChange={(e) => setAppPw(e.target.value)}
                  placeholder={cfg.has_app_password ? "•••• (kosongkan = tetap)" : "16 karakter"}
                  className="h-10 pr-9 bg-[hsl(220_12%_8%)]"
                  autoComplete="new-password"
                  data-testid="cc-gmail-app-password"
                />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary" aria-label="Tampilkan">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Cari email dalam (hari terakhir)</Label>
              <Input type="number" min={1} max={730} value={cfg.lookback_days} onChange={(e) => setCfg({ ...cfg, lookback_days: e.target.value })} className="mt-1 h-10 bg-[hsl(220_12%_8%)] num" data-testid="cc-gmail-lookback" />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Aturan pencarian email tagihan"
          description="Satu aturan per kartu/bank: kata kunci judul email, pengirim (opsional), dan password PDF-nya."
          actions={
            <Button size="sm" variant="outline" onClick={() => setCfg({ ...cfg, rules: [...cfg.rules, newRule()] })} data-testid="cc-rule-add">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Tambah aturan
            </Button>
          }
          testid="cc-gmail-rules"
        >
          {cfg.rules.length === 0 && (
            <div className="text-sm text-muted-foreground" data-testid="cc-rules-empty">
              Belum ada aturan. Contoh: judul <span className="text-foreground">"e-Statement Kartu Kredit"</span>, pengirim <span className="text-foreground">bca.co.id</span>.
            </div>
          )}
          <div className="space-y-3">
            {cfg.rules.map((r, i) => (
              <div key={r.key} className="rounded-lg border border-border bg-[hsl(220_12%_8%)] p-3 grid md:grid-cols-[1fr_1.4fr_1fr_1fr_auto] gap-2 items-end" data-testid={`cc-rule-${i}`}>
                <div>
                  <Label className="text-[11px] text-muted-foreground">Nama kartu</Label>
                  <Input value={r.label} onChange={(e) => setRule(r.key, { label: e.target.value })} placeholder="BCA Everyday" className="mt-1 h-9 bg-[hsl(220_12%_6%)]" data-testid={`cc-rule-label-${i}`} />
                </div>
                <div>
                  <Label className="text-[11px] text-muted-foreground">Kata kunci judul email *</Label>
                  <Input value={r.subject} onChange={(e) => setRule(r.key, { subject: e.target.value })} placeholder="e-Statement Kartu Kredit" className="mt-1 h-9 bg-[hsl(220_12%_6%)]" data-testid={`cc-rule-subject-${i}`} />
                </div>
                <div>
                  <Label className="text-[11px] text-muted-foreground">Pengirim (opsional)</Label>
                  <Input value={r.sender} onChange={(e) => setRule(r.key, { sender: e.target.value })} placeholder="bca.co.id" className="mt-1 h-9 bg-[hsl(220_12%_6%)]" data-testid={`cc-rule-sender-${i}`} />
                </div>
                <div>
                  <Label className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    Password PDF {r.has_password && !r.clear_password && <Badge variant="outline" className="text-[9px] border-[hsl(160_40%_30%)] text-[hsl(160_55%_60%)]">tersimpan</Badge>}
                  </Label>
                  <Input type="password" value={r.pdf_password} onChange={(e) => setRule(r.key, { pdf_password: e.target.value, clear_password: false })} placeholder={r.has_password ? "•••• (kosongkan = tetap)" : "mis. DDMMYY"} autoComplete="new-password" className="mt-1 h-9 bg-[hsl(220_12%_6%)]" data-testid={`cc-rule-password-${i}`} />
                </div>
                <Button size="icon" variant="ghost" className="h-9 w-9 text-muted-foreground hover:text-[hsl(0_72%_66%)]" onClick={() => setCfg({ ...cfg, rules: cfg.rules.filter((x) => x.key !== r.key) })} data-testid={`cc-rule-remove-${i}`} aria-label="Hapus aturan">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button onClick={save} disabled={saving} className="press" data-testid="cc-gmail-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Simpan pengaturan
            </Button>
            <Button variant="outline" onClick={test} disabled={testing || !cfg.has_app_password} data-testid="cc-gmail-test">
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plug className="h-4 w-4 mr-2" />} Tes koneksi Gmail
            </Button>
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Cara membuat App Password Gmail" eyebrow="Panduan" testid="cc-gmail-guide">
        <ol className="space-y-2.5 text-sm text-[hsl(45_15%_82%)] list-decimal pl-4 leading-relaxed">
          <li>Aktifkan <b>Verifikasi 2 Langkah</b> di akun Google Anda.</li>
          <li>
            Buka{" "}
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
              myaccount.google.com/apppasswords <ExternalLink className="h-3 w-3" />
            </a>
            , buat nama aplikasi mis. "kalkulatoreddy".
          </li>
          <li>Salin 16 karakter yang muncul dan tempel di kolom App Password, lalu simpan.</li>
          <li>Pastikan IMAP aktif: Gmail → Setelan → Lihat semua setelan → Penerusan dan POP/IMAP → <b>Aktifkan IMAP</b>.</li>
          <li>Tambahkan aturan per kartu, klik <b>Tes koneksi</b>, lalu di tab Tagihan klik <b>Tarik dari Gmail</b>.</li>
        </ol>
        <InfoNote className="mt-4">
          Akses hanya membaca email yang cocok dengan aturan; email tidak ditandai dibaca. Password Gmail & PDF disimpan terenkripsi. Password PDF bank umumnya kombinasi tanggal lahir (DDMMYY) dan/atau digit nomor kartu — cek email dari bank.
        </InfoNote>
      </SectionCard>
    </div>
  );
}
